import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, DONATION_TIERS } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

export const DonateModal = ({ open, onClose }) => {
  const { t } = useLang();
  const [tier, setTier] = useState("supporter");
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const isCustom = tier === "custom";

  const submit = async () => {
    let payload = { frequency, donor_name: name, donor_email: email, origin_url: window.location.origin };
    if (isCustom) {
      const amt = parseFloat(custom);
      if (!amt || amt < 1) { toast.error(t("donate.invalidAmount")); return; }
      payload.custom_amount = amt;
    } else {
      payload.tier = tier;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || t("donate.error"));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="donate-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A1128]/80 backdrop-blur-sm" onClick={onClose}>
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#F8F9FA] max-h-[90vh] overflow-y-auto">
            <div className="bg-[#0A1128] px-8 py-7 relative">
              <button data-testid="donate-modal-close" onClick={onClose} className="absolute top-5 right-5 text-white/60 hover:text-[#D4AF37] transition-colors"><X size={22} /></button>
              <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
                <Heart size={16} fill="#D4AF37" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t("donate.badge")}</span>
              </div>
              <h3 className="font-display text-3xl text-white">{t("donate.title")}</h3>
            </div>

            <div className="px-8 py-7 space-y-6">
              <div className="inline-flex border border-[#0A1128]/15 p-1">
                {[["one_time", t("donate.onetime")], ["monthly", t("donate.monthly")]].map(([id, label]) => (
                  <button key={id} data-testid={`freq-${id}`} onClick={() => setFrequency(id)}
                    className={`px-6 py-2 text-sm font-medium transition-colors ${frequency === id ? "bg-[#0A1128] text-white" : "text-[#4B5563]"}`}>{label}</button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {DONATION_TIERS.map((tr) => (
                  <button key={tr.id} data-testid={`tier-${tr.id}`} onClick={() => setTier(tr.id)}
                    className={`py-4 border text-center transition-all duration-200 ${tier === tr.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#0A1128]/15 hover:border-[#D4AF37]/50"}`}>
                    <span className="block font-display text-2xl text-[#0A1128]">${tr.amount}</span>
                    <span className="block text-xs text-[#4B5563] mt-1">{t(`donate.tierLabels.${tr.id}`)}</span>
                  </button>
                ))}
                <button data-testid="tier-custom" onClick={() => setTier("custom")}
                  className={`py-4 border text-center transition-all duration-200 ${isCustom ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#0A1128]/15 hover:border-[#D4AF37]/50"}`}>
                  <span className="block font-display text-2xl text-[#0A1128]">$…</span>
                  <span className="block text-xs text-[#4B5563] mt-1">{t("donate.custom")}</span>
                </button>
              </div>

              {isCustom && (
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4B5563] text-lg">$</span>
                  <input data-testid="custom-amount-input" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)}
                    placeholder={t("donate.customPh")} className="w-full pl-9 pr-4 py-3 bg-white border border-[#0A1128]/15 focus:border-[#D4AF37] outline-none text-lg" />
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <input data-testid="donor-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("donate.namePh")} className="w-full px-4 py-3 bg-white border border-[#0A1128]/15 focus:border-[#D4AF37] outline-none" />
                <input data-testid="donor-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("donate.emailPh")} className="w-full px-4 py-3 bg-white border border-[#0A1128]/15 focus:border-[#D4AF37] outline-none" />
              </div>

              <button data-testid="donate-submit-button" onClick={submit} disabled={loading}
                className="w-full py-4 bg-[#D4AF37] text-[#0A1128] font-semibold hover:bg-[#E5C158] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? (<><Loader2 className="animate-spin" size={18} /> {t("donate.redirecting")}</>) : t("donate.submit")}
              </button>
              <p className="text-center text-xs text-[#4B5563]">{t("donate.secure")}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
